/**
 * Generate Jig model-scene module.
 * Derives mesh-level scene metadata used by viewpoint planning, including stable
 * mesh ids, triangle counts, and the planner-ready scene context projection.
 */
import * as THREE from 'three'
import type { MemeGeometryRecoveryPayload, MemeMetaFile, ViewpointSceneContext, ViewpointSceneSource } from './generate-jig.types'
import { buildSceneContextForMeshNodes } from './viewpoint/scene-context'
import { normalizeViewpointSceneContext } from './viewpoint/scene-context-normalizer'
import { generateGuid } from './generate-jig.utils'

function roundValue(value: number): number {
  return parseFloat(value.toFixed(6))
}

function toVector3(value: THREE.Vector3) {
  return {
    x: roundValue(value.x),
    y: roundValue(value.y),
    z: roundValue(value.z),
  }
}

function getModelTriangleCount(meshNodes: THREE.Mesh[]): number {
  let triangleCount = 0

  meshNodes.forEach((mesh: THREE.Mesh) => {
    const geometry = (mesh as any).geometry
    if (!geometry) {
      return
    }

    if (geometry.index && typeof geometry.index.count === 'number') {
      triangleCount += Math.floor(geometry.index.count / 3)
      return
    }

    if (geometry.attributes && geometry.attributes.position && typeof geometry.attributes.position.count === 'number') {
      triangleCount += Math.floor(geometry.attributes.position.count / 3)
    }
  })

  return triangleCount
}

function collectMeshNodes(
  modelObject: THREE.Object3D,
  innerParentName: string
): { meshNodes: THREE.Mesh[]; objectToFullName: Map<THREE.Object3D, string> } {
  const objectToFullName = new Map<THREE.Object3D, string>()
  const meshNodes: THREE.Mesh[] = []

  modelObject.traverse((child: THREE.Object3D) => {
    const childAny = child as any
    const isMeshObject = (child instanceof THREE.Mesh || child.type === 'Mesh' || childAny.isMesh === true) && !!childAny.geometry

    if (!isMeshObject) {
      return
    }

    const meshName = child.name && child.name.length > 0 ? child.name : 'UnnamedMesh'
    const finalUid = generateGuid()
    const meshFullName = finalUid + '_' + meshName

    objectToFullName.set(child, meshFullName)
    meshNodes.push(child as THREE.Mesh)
  })

  if (meshNodes.length === 0) {
    throw new Error('No mesh found in OBJ file')
  }

  objectToFullName.set(modelObject, innerParentName)

  return { meshNodes, objectToFullName }
}

/**
 * Extracts mesh nodes and stable runtime names from the loaded OBJ scene so later
 * model and viewpoint steps can share the same mesh identity mapping.
 */
export function buildModelSceneSource(
  modelObject: THREE.Object3D,
  innerParentName: string
): ViewpointSceneSource {
  const { meshNodes, objectToFullName } = collectMeshNodes(modelObject, innerParentName)

  return {
    meshNodes,
    objectToFullName,
    triangleCount: getModelTriangleCount(meshNodes),
    modelOrigin: toVector3(modelObject.position),
  }
}

/**
 * Converts a previously collected scene source into the lightweight viewpoint
 * scene context expected by the planner.
 */
export function buildSceneContextFromSource(sceneSource: ViewpointSceneSource | null | undefined): ViewpointSceneContext | null {
  if (!sceneSource) {
    return null
  }

  return buildSceneContextForMeshNodes(sceneSource.meshNodes, sceneSource.objectToFullName)
}

export function buildPreparedSceneContextFromSource(
  sceneSource: ViewpointSceneSource | null | undefined,
  options?: {
    memeMetaData?: MemeMetaFile | null
    recoveredBounds?: MemeGeometryRecoveryPayload | null
  }
): ViewpointSceneContext | null {
  const rawSceneContext = buildSceneContextFromSource(sceneSource)
  if (!rawSceneContext) {
    return null
  }

  return normalizeViewpointSceneContext({
    baseSceneContext: rawSceneContext,
    modelOrigin: sceneSource ? sceneSource.modelOrigin : null,
    memeMetaData: options && typeof options.memeMetaData !== 'undefined' ? options.memeMetaData : null,
    recoveredBounds: options && typeof options.recoveredBounds !== 'undefined' ? options.recoveredBounds : null,
  })
}
